/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component, EventEmitter, Injectable, Pipe, PipeTransform } from '@angular/core';
import { DropdownModel, MenuConfigModel, NotificationModel } from '../models';
import { EN_US } from '../languages';
import { EndpointsModel } from '../../modules/centralisedManagement/shared/models';
import { convertToParamMap } from '@angular/router';
import { of, Subject } from 'rxjs';

@Injectable()
export class MockService {
    /**
     * Mock the api service
     */
    public apiService(): any {
        return (
            class MockApiService {
                get() {
                    return new Subject<any>();
                }
            }
        );
    }

    /**
     * Mock namespace service
     */
    public authStoreService(): any {
        return (
            class MockAuthStoreService {
                getLocalStorageLoginDetails(): any {
                    return {};
                }

                getSelectedModuleAuth(): any {
                    return {};
                }

                getCentralManagementAuth(): boolean {
                    return true;
                }

                setAuthModule(): void {

                }
            }
        );
    }

    /**
     * Mock new Date().
     * @param dateString e.g. "2000-12-25T03:24:00"
     */
    public dateService(dateString?: string): any {
        return dateString ? new Date(dateString) : new Date();
    }

    /**
     * Mock user service.
     * Note: please do not change the applianceId; this will
     * break the add-edit-fips-group spec file.
     */
    public downloadService(): any {
        return (
            class MockDownloadService {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                generateDownload(bloc: Blob, fileName: string, options: any): void {
                }
            }
        );
    }

    /**
     * Mock get current navigation
     */
    public getCurrentNavigation(navigationState?: any): any {
        const state = navigationState ? navigationState : { user: 'loadbalancer' };

        return (
            class MockGetCurrentNavigation {
                getCurrentNavigation() {
                    return {
                        extras: {
                            state
                        }
                    };
                }
            }
        );
    }

    /**
     * Mock language service.
     */
    public languageService(): any {
        return (
            class MockLanguageService {
                dictionary = { ['en-us']: EN_US };
                lookup = this.dictionary['en-us'];

                translate(key: string) {
                    if (this.lookup[key]) {
                        return this.lookup[key];
                    }

                    return '**' + key + '**';
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                setLanguage(language: string) {

                }
            }
        );
    }

    /**
     * Mock log service
     */
    public logService(): any {
        return (
            class MockLogService {
                enabled: boolean = false;

                debug(): void {
                }

                error(): void {
                }

                warn(): void {
                }

                toggle(): void {
                    this.enabled = !this.enabled;
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                setEnvironment(production) {

                }
            }
        );
    }

    /**
     * Mock modal service.
     */
    public modalService(): any {
        return (
            class MockModalService {
                openParentModalEmitter: Subject<boolean> = new Subject<boolean>();
                closeParentModalEmitter: Subject<boolean> = new Subject<boolean>();
                closeAllParentModalEmitter: Subject<boolean> = new Subject<boolean>();
                cancelSubmission: Subject<boolean> = new Subject<boolean>();

                openModalInParent() {

                }

                findModal(id: string, openState: boolean): any {
                    return { id: id, isOpen: openState };
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                confirmDecision(confirmationObject: any): void {
                    return;
                }
            }
        );
    }

    /**
     * Mock navigation service.
     */
    public navigationService(): any {
        return (
            class NavigationService {
                _redirectLocation: string = '';

                getEnduranceUrl() {
                    return '';
                }

                getApplianceId() {
                    return '';
                }

                set redirectLocation(location: string) {
                    this._redirectLocation = location;
                }

                get redirectLocation(): string {
                    return this._redirectLocation;
                }

                cleanEnduranceUrl(url: string): string {
                    return url.replace('/lb/', '');
                }
            }
        );
    }

    /**
     * Mock networking api service
     */
    public notifyService(): any {
        return (
            class NotifyService {
                addToToasterEmitter: Subject<NotificationModel> = new Subject<NotificationModel>();
                clearToasterEmitter: Subject<boolean> = new Subject<boolean>();
                removeFromToasterEmitter: Subject<NotificationModel> = new Subject<NotificationModel>();
                updateNotificationsEmitter: Subject<NotificationModel> = new Subject<NotificationModel>();

                muteLocalNotifications(): void {
                }

                unmuteLocalNotifications(): void {
                }

                getCopyOfNotifications(): any {
                    return [];
                }

                removeNotificationById(): void {
                }

                error(): boolean {
                    return true;
                }

                warning() {
                    return true;
                }

                clearLocal(): void {

                }
            }
        );
    }

    /**
     * Mock activated route response, pass in an object that reflects the URL param e.g. { id: "10" }
     * @param params
     */
    public params(params: any): any {
        return { snapshot: { paramMap: convertToParamMap(params) } };
    }

    /**
     * Mock activated route response, pass in a string.
     * @param params
     */
    public queryParams(params: string): any {
        return { queryParams: of({ params }) };
    }

    /**
     * Mock pipe service.
     * @param name
     */
    public pipe(name: string): Pipe {
        const metadata: Pipe = {
            name
        };

        return Pipe(metadata)(
            class MockPipe implements PipeTransform {
                transform(string: string): string {
                    return string;
                }
            }
        );
    }

    /**
     * Mock rbac service.
     */
    public router(): any {
        return (
            class MockRouter {
                getCurrentNavigation() {
                    return {
                        extras: {
                            state: {
                                locationId: 'someId',
                                locationName: 'someName'
                            }
                        }
                    };
                }
            }
        );
    }

    /**
     * Mock rbac service.
     */
    public rbacService(): any {
        return (
            class MockRbacService {
                requestedUrlEmitter: Subject<any> = new Subject<any>();

                _endpointsLookup = [
                    {
                        'path': '/api/v1/accounts/:id/2fa/verify',
                        'tier': true,
                        'methods': { 'POST': true }
                    },
                    {
                        'path': '/api/v1/organisations/:orgid/saml',
                        'tier': true,
                        'methods': { 'DELETE': true, 'POST': true }
                    },
                    {
                        'path': '/api/v1/accounts/:id/:token/invite',
                        'tier': true,
                        'methods': { 'GET': true }
                    },
                    {
                        'path': '/api/v1/news/:news',
                        'tier': false,
                        'methods': { 'GET': false }
                    }
                ];

                getPageRequiredRoute() {

                }

                getPageRequiredRoutes(paths: string[]): EndpointsModel[] {
                    return this._endpointsLookup.filter((item: EndpointsModel) => paths.includes(item.path));
                }

                getPermissionsLookup(buttonReference: any): void {
                    return buttonReference;
                }

                disableForm(permission: any): boolean {
                    return permission;
                }

                setMenuPermissions(menu: MenuConfigModel) {
                    return menu;
                }

                getLookupObject() {
                    return '';
                }
            }
        );
    }

    /**
     * Mock socket service.
     */
    public socketService(): any {
        return (
            class MockSocketService {
                socketClosedEmitter: EventEmitter<any> = new EventEmitter();

                connect() {
                    return new Subject<any>();
                }

                on() {
                    return new Subject<any>();
                }

                emit() {

                }

                close() {

                }
            }
        );
    }

    /**
     * Mock user service.
     * Note: please do not change the applianceId; this will
     * break the add-edit-fips-group spec file.
     */
    public userService(): any {
        return (
            class MockUserService {
                user = {
                    'userLogin': 'devlist@loadbalancer.org',
                    'id': '1',
                    'permissions': 2,
                    'applianceAddress': 'kodiak.lborg.dev',
                    'applianceId': 'd4b-a3d-75d-b7c',
                    'appliancePort': 443,
                    'selected': true,
                    'organisationId': 2,
                    'ssoLogin': false,
                    'ssoProvider': null
                };

                login(): void {

                }

                getUser(property?: PropertyKey) {
                    if (property) {
                        if (this.user.hasOwnProperty(property)) {
                            return this.user[property];
                        }
                    } else {
                        return this.user;
                    }
                }

                tokenStatus() {

                }

                logOutEmitter: Subject<any> = new Subject<any>();
            }
        );
    }

    /**
     * Mock user service.
     * Note: please do not change the applianceId; this will
     * break the add-edit-fips-group spec file.
     */
    public utilsService(): any {
        return (
            class MockUtilsService {
                /**
                 * Debounce timer ID, needs to be class level as the function needs to reference it.
                 * @private
                 */
                private debounceTimerId = {};

                convertNameToSingular(label: string, index?: number): string {
                    const labelMinusS: string = label.endsWith('ies')
                        ? label.replace('ies', 'y')
                        : label.endsWith('s') ? label.slice(0, -1) : label;
                    const formattedLabel: string = labelMinusS.endsWith('\'') ? labelMinusS.slice(0, -1) : labelMinusS;

                    return index ? formattedLabel + ' ' + index : formattedLabel;
                }

                getObjectValue(object: any, propertiesPath: string) {
                    return propertiesPath
                        // Remove square brackets from path e.g. [0]
                        .replace(/\[/g, '.')
                        .replace(/]/g, '')
                        // Create an array from the path
                        .split('.')
                        // Reduce the array checking each part of the object path.
                        .reduce((o, k) => (o || {})[k], object);
                }

                duplicateObject(obj: object): any {
                    return JSON.parse(JSON.stringify(obj));
                }

                createDateTimeString(isoDate: string | number | Date, includeTime: boolean = true): string {
                    const dateTime = new Date(isoDate);
                    const locale = Intl.DateTimeFormat().resolvedOptions().locale;

                    return dateTime.toLocaleDateString(locale) + (includeTime ? ' ' + dateTime.toLocaleTimeString(locale) : '');
                }

                sortArrayByProperty(array: any[], property: string, order?: any[]): any {
                    if (order) {
                        return array.sort(function (a, b) {
                            return order.indexOf(a[property]) - order.indexOf(b[property]);
                        });
                    } else {
                        return array.sort((a, b) => {
                            if (a[property] > b[property]) {
                                return 1;
                            } else if (b[property] > a[property]) {
                                return -1;
                            }

                            return 0;
                        });
                    }
                }

                replaceLastCommaWithAnd(string: string, stringContainSpaces: boolean = false): string {
                    const cleanedString = this.cleanStringForFormatting(string, stringContainSpaces);

                    return cleanedString.replace(/,(?=[^,]*$)/, ' and');
                }

                debounce(timerId: string, callBack: (...args: any[]) => void, delay = 250) {
                    if (this.debounceTimerId[timerId]) {
                        clearTimeout(this.debounceTimerId[timerId]);
                    }
                    this.debounceTimerId[timerId] = setTimeout(() => {
                        callBack();
                        delete this.debounceTimerId[timerId];
                    }, delay);
                }

                dedupe(array: any[]) {
                    const primitives = { 'boolean': {}, 'number': {}, 'string': {} }, objs = [];

                    return array.filter(item => {
                        const type = typeof item;
                        if (type in primitives) {
                            return primitives[type].hasOwnProperty(item) ? false : (primitives[type][item] = true);
                        } else {
                            return objs.indexOf(item) >= 0 ? false : objs.push(item);
                        }
                    });
                }

                isServiceId(value: string):
                    boolean {
                    const regexPattern = /[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}/;

                    return regexPattern.test(value);
                }

                replaceLastCommaWithOr(string: string, stringContainSpaces: boolean = false): string {
                    const cleanedString = this.cleanStringForFormatting(string, stringContainSpaces);

                    return cleanedString.replace(/,(?=[^,]*$)/, ' or');
                }

                cleanStringForFormatting(string: string, stringContainSpaces: boolean = false):
                    string {
                    const cleanedString = string.replace(/ or | and /g, ',');
                    const removedSpaces = stringContainSpaces ? cleanedString : cleanedString.replace(/\s/g, '');

                    return removedSpaces.replace(/,/g, ', ');
                }

                convertStringToId(string: string): string {
                    let stringToConvert = string;
                    // remove first and last slashes if url path
                    stringToConvert = stringToConvert.replace(/^\/|\/$/g, '');
                    // Check if string is camelcase (doesn't contain spaces) and not all in capitol letters.
                    if (!stringToConvert.includes(' ') && !/^[A-Z]*$/.test(stringToConvert)) {
                        stringToConvert = stringToConvert.replace(/([a-z0-9\w])([A-Z])/g, '$1 $2');
                    }

                    return stringToConvert.toLowerCase().replace(/[\s_\/]/g, '-');
                }

                decodeJWT(token) {
                    const base64Url = token.split('.')[1];
                    if (base64Url) {
                        const base64 = base64Url.replace('-', '+').replace('_', '/');
                        let data = '';
                        try {
                            data = JSON.parse(window.atob(base64));
                        } catch (error) {
                            return null;
                        }

                        return data;
                    } else {
                        return null;
                    }
                }

                cleanArray(array: any[]): any {
                    return array.filter(
                        item => (item !== null && item !== undefined) &&
                            (
                                (typeof item === 'string' && item !== '') ||
                                (typeof item === 'object' && Object.keys(item).length) ||
                                (typeof item === 'number')
                            )
                    );
                }

                countArrayDuplicates(array: any): any {
                    const counts = {};
                    array.forEach((item: string | number) => {
                        counts[item] = (counts[item] || 0) + 1;
                    });

                    return counts;
                }

                getCountId(counts: any, sortBy: 'most' | 'least'): string {
                    const sortedCounts = this.sortArray(Object.values(counts));
                    const counter = sortBy === 'most' ? sortedCounts.reverse()[0] : sortedCounts[0];
                    for (const count in counts) {
                        if (counts.hasOwnProperty(count)) {
                            if (counts[count] === counter) {
                                return count;
                            }
                        }
                    }
                }

                compareDecimals(firstNumber: string, secondNumber: string, operation: string):
                    boolean {
                    const a: number = parseFloat(firstNumber);
                    const b: number = parseFloat(secondNumber);

                    switch (operation) {
                        case '<':
                            return a < b;
                        case '<=':
                            return a <= b;
                        case '>':
                            return a > b;
                        case '>=':
                            return a >= b;
                        case '===':
                            return a === b;
                    }
                }

                getExtensionFromFilename(fileName: string): string | null {
                    if (!fileName) {
                        return null;
                    }

                    const fileParts: string[] = fileName.split('.');
                    if (fileParts.length > 1) {
                        return fileParts[fileParts.length - 1].toLowerCase();
                    }

                    return null;
                }

                sortArray(array: any[]) {
                    return array.sort((a, b) => a - b);
                }

                validateUrl(urlString: string) {
                    let url: URL;
                    try {
                        url = new URL(urlString);
                    } catch (_) {
                        return false;
                    }

                    return !!url.href;
                }

                removeHTMLFromString(string: string): string {
                    return string.replace(/<\/?[^>]+(>|$)/g, '').replace(/^\s+|\s+$/gm, '');
                }

                hasFormDataChanged(initialFormData: any, newFormData: any, exclude ?: any[]): boolean {
                    if ((initialFormData && Object.keys(initialFormData).length) && (newFormData && Object.keys(newFormData).length)) {
                        const changes = [];
                        for (const property in newFormData) {
                            if (newFormData.hasOwnProperty(property)) {
                                if (Array.isArray(newFormData[property])) {
                                    if (!this.compareArrays(initialFormData[property], newFormData[property])) {
                                        changes.push(property);
                                    }
                                } else if (initialFormData.hasOwnProperty(property)) {
                                    if (newFormData[property] !== initialFormData[property]) {
                                        changes.push(property);
                                    }
                                } else if (exclude && exclude.includes(property) && newFormData[property] !== null) {
                                    changes.push(property);
                                }
                            }
                        }

                        return !!changes.length;
                    }
                }

                compareArrays(array1: any[], array2: string | any[]) {
                    return Array.isArray(array1) &&
                        Array.isArray(array2) &&
                        array1.length === array2.length &&
                        array1.every((val, index) => val === array2[index]);
                }

                getDateTimeObjectFromIsoDateTime(isoDateTime: string): any {
                    const dateTimeParts = isoDateTime.split('T');
                    // Account for the +/- timezone shift; iso date/time format is always the same. Use slice.
                    const time = dateTimeParts[1].slice(0, 5);
                    const timeOffset = dateTimeParts[1].slice(8);

                    return {
                        date: dateTimeParts[0],
                        time: time,
                        timeOffset: timeOffset
                    };
                }

                createArrayIfNotExist(arr: any, name: string): void {
                    if (!
                        arr[name]
                    ) {
                        arr[name] = [];
                    }
                }

                createRangeArray(from: number, to: number): any[] {
                    const input = [];
                    for (let i = from; i >= to; i--) {
                        input.push({
                            key: i,
                            value: i
                        });
                    }

                    return input;
                }

                typeOfIncludingArray(value: any): string {
                    if (Array.isArray(value)) {
                        return 'array';
                    } else {
                        return typeof value;
                    }
                }

                findIndexReversed(array: any[], objectProperty: string, value ?: string) {
                    // This needs to be in reverse, so we can't use other array methods.
                    for (let index = array.length - 1; index >= 0; index--) {
                        const item = array[index];
                        if (item.hasOwnProperty(objectProperty)) {
                            if (value && item[objectProperty] === value) {
                                return index;
                            } else if (!value && item[objectProperty]) {
                                return index;
                            }
                        }
                    }

                    return -1;
                }

                convertArrayToDropDown(array: any, propertyForKey = 'id', propertyForValue = 'value'): DropdownModel[] {
                    const dropdown = [];
                    if (array?.length) {
                        for (const item of array) {
                            const { key, value } = this.getKeyAndValue(item, propertyForKey, propertyForValue);
                            if (key && value) {
                                dropdown.push(<DropdownModel>{ key, value, });
                            }
                        }
                    }

                    return dropdown;
                }

                getKeyAndValue(item: any, keyToUse: string, valueToUse: string): DropdownModel {
                    let returnKey: string, returnValue: string;
                    const key = item[keyToUse] ? item[keyToUse] : item.id ? item.id : item;
                    const value = item[valueToUse] ? item[valueToUse] : item.value ? item.value : item;
                    if ((typeof key !== 'object' && !Array.isArray(key)) &&
                        (typeof value !== 'object' && !Array.isArray(value))) {
                        returnKey = key;
                        returnValue = value;
                    } else {
                        if (!Array.isArray(key) && Object.keys(item).length === 1) {
                            returnKey = item[Object.keys(item)[0]];
                            returnValue = item[Object.keys(item)[0]];
                        } else if (Array.isArray(key) && key.length === 1) {
                            returnKey = item[0];
                            returnValue = item[0];
                        }
                    }

                    return { key: returnKey, value: returnValue };
                }

                generateRandomString(): string {
                    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
                }

                findDuplicates(array: any[]): any[] {
                    return array.filter((item, index: number) => array.indexOf(item) !== index);
                }

                localStoragePresent(): boolean {
                    const test = 't141-ls-test';
                    try {
                        window.localStorage.setItem(test, test);
                        window.localStorage.removeItem(test);

                        return true;
                    } catch (e) {
                        return false;
                    }
                }
            }
        )
            ;
    }

    // eslint-disable-next-line max-len
    loadbalancerJwt = 'ewogICAgInR5cCI6ICJKV1QiLAogICAgImFsZyI6ICJIUzI1NiIKfQ.ewogICAgImlzcyI6ICJkMTUtYjRmLThiYy1kMjgiLAogICAgInVzZXJuYW1lIjogImxvYWRiYWxhbmNlciIsCiAgICAicGVlciI6IGZhbHNlLAogICAgInZlcnNpb24iOiAiOS4wLjMuMTQxOS5nZTlkZmJmMjRmIiwKICAgICJnZW4iOiAxNjc5MDY4NjQ5LAogICAgImV4cCI6IDE2NzkwNzA0NDkKfQ.hOgeA14YsoiMziNU9ppog-7o-rTsV9dZTQLDXgnvXE8';
    // eslint-disable-next-line max-len
    portalJwt = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImRldmxpc3RAbG9hZGJhbGFuY2VyLm9yZyIsImV4cCI6MTY3OTMxNDkzNywiZ2VuIjoxNjc5MzA3MTM3LCJpZCI6MSwiaXNzIjoibGJDZW50cmFsIiwibGJJRCI6IiIsIm9yZ0lEIjoyLCJwZWVyIjpmYWxzZSwicGVybWlzc2lvbnMiOjIsInNzb1Byb3ZpZGVyIjoiIiwidmVyc2lvbiI6IjEuMC4wIn0.K3z_uR6Vn0EqNlxKc-OrVSstZ5vp5s38-t6-2rOKkUc';
}

/**
 * @summary Create a mocked component for the validation directive.
 */
@Component({
    selector: 'app-mock-component',
    standalone: true,
    template: `
        <form>
            <input type="text" [name]="fieldName">
            <textarea [name]="textAreaName"></textarea>
        </form>`
})
export class MockFormComponent {
    public fieldName: string = 'testFieldName';
    public textAreaName: string = 'testTextAreaName';
}
